/**
 * Ad Form Page (Create/Edit)
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Avatar,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
  useGetAdAdminQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
} from '../../store/api/adsApi';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';

const AdFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: adData, isLoading: isLoadingAd } = useGetAdAdminQuery(Number(id!), {
    skip: !isEdit,
  });

  const [createAd, { isLoading: isCreating }] = useCreateAdMutation();
  const [updateAd, { isLoading: isUpdating }] = useUpdateAdMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    image: null as File | null,
    video: null as File | null,
    link_url: '',
    link_text: '',
    link_text_ar: '',
    is_active: true,
    order: 0,
    start_date: '',
    end_date: '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  // Load ad data when editing
  useEffect(() => {
    if (isEdit && adData?.data) {
      const ad = adData.data;
      setFormData({
        title: ad.title,
        title_ar: ad.title_ar || '',
        description: ad.description || '',
        description_ar: ad.description_ar || '',
        image: null,
        video: null,
        link_url: ad.link_url || '',
        link_text: ad.link_text || '',
        link_text_ar: ad.link_text_ar || '',
        is_active: ad.is_active,
        order: ad.order,
        // Locale 'en' forced: datetime-local inputs only accept Latin digits.
        start_date: ad.start_date ? moment(ad.start_date).locale('en').format('YYYY-MM-DDTHH:mm') : '',
        end_date: ad.end_date ? moment(ad.end_date).locale('en').format('YYYY-MM-DDTHH:mm') : '',
      });
      setImagePreview(ad.image || null);
      setVideoPreview(ad.video || null);
    }
  }, [isEdit, adData]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, video: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      if (formData.title_ar) formDataToSend.append('title_ar', formData.title_ar);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.description_ar) formDataToSend.append('description_ar', formData.description_ar);
      if (formData.image) formDataToSend.append('image', formData.image);
      if (formData.video) formDataToSend.append('video', formData.video);
      if (formData.link_url) formDataToSend.append('link_url', formData.link_url);
      if (formData.link_text) formDataToSend.append('link_text', formData.link_text);
      if (formData.link_text_ar) formDataToSend.append('link_text_ar', formData.link_text_ar);
      formDataToSend.append('is_active', formData.is_active.toString());
      formDataToSend.append('order', formData.order.toString());
      if (formData.start_date) formDataToSend.append('start_date', formData.start_date);
      if (formData.end_date) formDataToSend.append('end_date', formData.end_date);

      if (isEdit) {
        await updateAd({ id: Number(id!), data: formDataToSend }).unwrap();
        toast.success(t('ads.toast.updateSuccess'));
      } else {
        await createAd(formDataToSend).unwrap();
        toast.success(t('ads.toast.createSuccess'));
      }
      navigate('/ads');
    } catch (err: any) {
      toast.error(err?.data?.message || (isEdit ? t('ads.toast.updateError') : t('ads.toast.createError')));
    }
  };

  if (isEdit && isLoadingAd) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('ads.form.editTitle') : t('ads.form.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.ads'), path: '/ads' },
          { label: isEdit ? t('common.edit') : t('common.create') },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/ads')}
        sx={{ mb: 3 }}
      >
        {t('ads.buttons.backToAds')}
      </Button>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('ads.form.basicInformation')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label={t('ads.form.titleEn')}
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label={t('ads.form.titleAr')}
                  value={formData.title_ar}
                  onChange={(e) => handleInputChange('title_ar', e.target.value)}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={`${t('ads.form.descriptionEn')} *`}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('ads.form.descriptionAr')}
                  value={formData.description_ar}
                  onChange={(e) => handleInputChange('description_ar', e.target.value)}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Media */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PhotoLibraryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('ads.detail.media')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={500} gutterBottom sx={{ mb: 2 }}>
                    {t('ads.form.image')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={imagePreview || undefined}
                      variant="rounded"
                      sx={{ width: 120, height: 120, border: '1px solid', borderColor: 'divider' }}
                    >
                      <ImageIcon sx={{ fontSize: 50 }} />
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button variant="outlined" component="label" size="medium">
                        {imagePreview ? t('ads.buttons.changeImage') : t('ads.buttons.uploadImage')}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      {imagePreview && (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, image: null }));
                            setImagePreview(null);
                          }}
                        >
                          {t('ads.buttons.remove')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <ImageSizeHint size={IMAGE_SIZES.banner} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={500} gutterBottom sx={{ mb: 2 }}>
                    {t('ads.form.video')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {videoPreview ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            maxWidth: '100%',
                          }}
                        >
                          <video
                            src={videoPreview}
                            controls
                            style={{ width: '100%', maxHeight: 200, display: 'block' }}
                          />
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="medium"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, video: null }));
                            setVideoPreview(null);
                          }}
                        >
                          {t('ads.buttons.removeVideo')}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<VideoIcon />}
                        size="medium"
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {t('ads.buttons.uploadVideo')}
                        <input
                          type="file"
                          hidden
                          accept="video/*"
                          onChange={handleVideoChange}
                        />
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Display Settings */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('ads.form.displaySettings')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                    }
                    label={t('ads.status.ACTIVE')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {t('ads.form.activeHelper')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  label={t('ads.form.displayOrder')}
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  helperText={t('ads.form.displayOrderHelper')}
                />
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('ads.form.startDateOptional')}
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('ads.form.startDateHelper')}
                />
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('ads.form.endDateOptional')}
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('ads.form.endDateHelper')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/ads')}
                  disabled={isCreating || isUpdating}
                  size="large"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isCreating || isUpdating || !formData.title}
                  size="large"
                >
                  {isCreating || isUpdating ? t('ads.buttons.saving') : isEdit ? t('ads.buttons.updateAd') : t('ads.buttons.createAd')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </form>
    </Box>
  );
};

export default AdFormPage;

